import { useState, useEffect, useRef } from 'react';
import {
  Plus,
  Trash2,
  ScrollText,
  Sparkles,
  X,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Wand2,
} from 'lucide-react';
import { LoadingState, Spinner } from '@systemdox/ui';
import {
  rulesPromptsApi,
  RULE_CATEGORIES,
  type Rule,
  type RuleCategory,
} from '@/services/rulesPromptsApi';
import {
  aiApi,
  type RuleConflictResult,
  type RuleConflictSuggestion,
  type RuleRefactorResult,
} from '@/services/aiApi';
import { cn } from '@/lib/utils';

const CATEGORY_COLORS: Record<RuleCategory, string> = {
  deployment:
    'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800',
  architecture:
    'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800',
  security:
    'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800',
  testing:
    'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800',
  process:
    'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800',
  other:
    'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
};

const RELATION_BADGES: Record<string, string> = {
  duplicate:
    'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800',
  conflict:
    'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800',
  overlap:
    'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800',
};

export default function RulesTab() {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);

  // Rule form state
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [ruleText, setRuleText] = useState('');
  const [ruleCategory, setRuleCategory] = useState<RuleCategory>('architecture');
  const [savingRule, setSavingRule] = useState(false);
  const [conflictResult, setConflictResult] = useState<RuleConflictResult | null>(null);

  // Category collapse + new-rule highlight state
  const [collapsedCategories, setCollapsedCategories] = useState<Set<RuleCategory>>(new Set());
  const [highlightedRuleId, setHighlightedRuleId] = useState<string | null>(null);
  const highlightTimer = useRef<number | null>(null);

  // Refactor-existing-rules state
  const [refactoring, setRefactoring] = useState(false);
  const [refactorResult, setRefactorResult] = useState<RuleRefactorResult | null>(null);
  const [selectedProposals, setSelectedProposals] = useState<Set<number>>(new Set());
  const [applyingRefactor, setApplyingRefactor] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    rulesPromptsApi
      .listRules()
      .then(setRules)
      .catch(() => setError('Failed to load rules.'))
      .finally(() => setLoading(false));
  }, []);

  /** Collapse every other category and briefly highlight the rule so the
      user can immediately see where it went and review it. */
  function focusOnRule(rule: Rule) {
    setCollapsedCategories(
      new Set(RULE_CATEGORIES.map((c) => c.value).filter((v) => v !== rule.category)),
    );
    setHighlightedRuleId(rule.id);
    if (highlightTimer.current) window.clearTimeout(highlightTimer.current);
    highlightTimer.current = window.setTimeout(() => setHighlightedRuleId(null), 4000);
  }

  useEffect(() => {
    return () => {
      if (highlightTimer.current) window.clearTimeout(highlightTimer.current);
    };
  }, []);

  function toggleCategory(category: RuleCategory) {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }

  async function saveNewRule(text: string, category: RuleCategory) {
    const rule = await rulesPromptsApi.addRule({ text, category });
    setRules((prev) => [rule, ...prev]);
    setRuleText('');
    setShowRuleForm(false);
    setConflictResult(null);
    focusOnRule(rule);
  }

  async function handleAddRule() {
    if (!ruleText.trim()) return;
    setSavingRule(true);
    setError(null);
    try {
      // Validate against existing rules and get optimized wording first;
      // fail open (store as written) if the check errors.
      let check: RuleConflictResult | null = null;
      try {
        check = await aiApi.analyzeRuleConflicts(ruleText.trim(), ruleCategory);
      } catch {
        check = null;
      }
      const hasOptimization =
        !!check?.optimizedText && check.optimizedText.trim() !== ruleText.trim();
      if (check && (check.findings.length > 0 || hasOptimization)) {
        setConflictResult(check);
        return;
      }
      await saveNewRule(ruleText.trim(), ruleCategory);
    } catch {
      setError('Failed to add rule. Please try again.');
    } finally {
      setSavingRule(false);
    }
  }

  async function handleAddAnyway() {
    setSavingRule(true);
    setError(null);
    try {
      await saveNewRule(ruleText.trim(), ruleCategory);
    } catch {
      setError('Failed to add rule. Please try again.');
    } finally {
      setSavingRule(false);
    }
  }

  async function handleAddOptimized() {
    if (!conflictResult?.optimizedText) return;
    setSavingRule(true);
    setError(null);
    try {
      await saveNewRule(conflictResult.optimizedText.trim(), ruleCategory);
    } catch {
      setError('Failed to add rule. Please try again.');
    } finally {
      setSavingRule(false);
    }
  }

  async function handleApplySuggestion(suggestion: RuleConflictSuggestion) {
    setSavingRule(true);
    setError(null);
    try {
      if (suggestion.action === 'update_existing' && suggestion.ruleId) {
        const updated = await rulesPromptsApi.updateRule(suggestion.ruleId, {
          text: suggestion.suggestedText,
        });
        setRules((prev) => prev.map((r) => (r.id === suggestion.ruleId ? updated : r)));
        setRuleText('');
        setShowRuleForm(false);
        setConflictResult(null);
        focusOnRule(updated);
      } else {
        await saveNewRule(suggestion.suggestedText, ruleCategory);
      }
    } catch {
      setError('Failed to apply suggestion. Please try again.');
    } finally {
      setSavingRule(false);
    }
  }

  async function handleRefactorRules() {
    setRefactoring(true);
    setError(null);
    try {
      const result = await aiApi.refactorRules();
      setRefactorResult(result);
      setSelectedProposals(new Set(result.proposals.map((_, i) => i)));
    } catch {
      setError('Failed to analyze rules. Please try again.');
    } finally {
      setRefactoring(false);
    }
  }

  function toggleProposal(index: number) {
    setSelectedProposals((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  async function handleApplyRefactor() {
    if (!refactorResult) return;
    setApplyingRefactor(true);
    setError(null);
    const failures: number[] = [];
    for (const [index, proposal] of refactorResult.proposals.entries()) {
      if (!selectedProposals.has(index)) continue;
      try {
        if (proposal.action === 'delete') {
          await rulesPromptsApi.deleteRule(proposal.ruleId);
          setRules((prev) => prev.filter((r) => r.id !== proposal.ruleId));
        } else if (proposal.text) {
          const updated = await rulesPromptsApi.updateRule(proposal.ruleId, {
            text: proposal.text,
            category: (proposal.category ?? proposal.currentCategory) as RuleCategory,
          });
          setRules((prev) => prev.map((r) => (r.id === proposal.ruleId ? updated : r)));
        }
      } catch {
        failures.push(index);
      }
    }
    setApplyingRefactor(false);
    if (failures.length > 0) {
      setError(`Failed to apply ${failures.length} of the selected changes. Please try again.`);
      // Keep the dialog open showing only the failed proposals still selected.
      setSelectedProposals(new Set(failures));
    } else {
      setRefactorResult(null);
      setSelectedProposals(new Set());
    }
  }

  async function handleDeleteRule(id: string) {
    setError(null);
    try {
      await rulesPromptsApi.deleteRule(id);
      setRules((prev) => prev.filter((r) => r.id !== id));
    } catch {
      setError('Failed to delete rule. Please try again.');
    }
  }

  // Group rules by category
  const groupedRules = rules.reduce<Record<string, Rule[]>>((acc, rule) => {
    if (!acc[rule.category]) acc[rule.category] = [];
    acc[rule.category].push(rule);
    return acc;
  }, {});

  if (loading) {
    return <LoadingState size="lg" />;
  }

  return (
    <div className="space-y-12">
      {error && (
        <div className="flex items-center justify-between px-4 py-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <section>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-teal-600" />
              Project Rules
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Define rules and principles that AI and your team must follow.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {rules.length > 1 && (
              <button
                onClick={handleRefactorRules}
                disabled={refactoring}
                title="Find duplicate, conflicting, or poorly worded rules and propose fixes"
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-teal-600 text-teal-700 dark:text-teal-300 text-sm font-medium hover:bg-teal-50 dark:hover:bg-teal-900/20 disabled:opacity-50"
              >
                {refactoring ? (
                  <Spinner size="sm" tone="current" />
                ) : (
                  <Wand2 className="w-4 h-4" />
                )}
                {refactoring ? 'Analyzing…' : 'Refactor Rules'}
              </button>
            )}
            <button
              onClick={() => setShowRuleForm(!showRuleForm)}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-700"
            >
              <Plus className="w-4 h-4" /> Add Rule
            </button>
          </div>
        </div>

        {/* Add rule form */}
        {showRuleForm && (
          <div className="mb-6 p-4 rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50/50 dark:bg-teal-900/10">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Add Rule
              </h3>
              <button
                onClick={() => setShowRuleForm(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                  Rule *
                </label>
                <input
                  value={ruleText}
                  onChange={(e) => setRuleText(e.target.value)}
                  placeholder='e.g. "Pipeline is idempotent — safe on first deploy and all subsequent re-deploys"'
                  className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block">
                  Category
                </label>
                <select
                  value={ruleCategory}
                  onChange={(e) => setRuleCategory(e.target.value as RuleCategory)}
                  className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {RULE_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <button
                onClick={handleAddRule}
                disabled={savingRule || !ruleText.trim()}
                className="flex items-center gap-2 px-4 py-2 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
              >
                {savingRule && <Spinner size="sm" tone="current" />}
                Add
              </button>
            </div>
          </div>
        )}

        {/* Rules list */}
        {rules.length === 0 && !showRuleForm ? (
          <div className="text-center py-12">
            <ScrollText className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto mb-4" />
            <p className="text-sm text-slate-500">No rules defined yet.</p>
            <p className="text-xs text-slate-400 mt-1">
              Add rules and principles for AI and your team to follow.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {RULE_CATEGORIES.filter((c) => groupedRules[c.value]?.length).map((cat) => {
              const isCollapsed = collapsedCategories.has(cat.value);
              return (
                <div key={cat.value}>
                  <button
                    onClick={() => toggleCategory(cat.value)}
                    className="flex items-center gap-1 text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 hover:text-slate-700 dark:hover:text-slate-300"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="w-3.5 h-3.5" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5" />
                    )}
                    {cat.label} ({groupedRules[cat.value].length})
                  </button>
                  {!isCollapsed && (
                    <div className="space-y-2">
                      {groupedRules[cat.value].map((rule) => (
                        <div
                          key={rule.id}
                          className={cn(
                            'group flex items-start justify-between gap-3 px-4 py-3 rounded-lg border transition-shadow',
                            CATEGORY_COLORS[rule.category],
                            rule.id === highlightedRuleId &&
                              'ring-2 ring-teal-500 dark:ring-teal-400',
                          )}
                        >
                          <p className="text-sm font-medium leading-relaxed">{rule.text}</p>
                          <button
                            onClick={() => handleDeleteRule(rule.id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5 text-current hover:text-red-600"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Refactor rules dialog ── */}
      {refactorResult && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setRefactorResult(null)}
        >
          <div
            className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-teal-500 shrink-0" />
                <div>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    {refactorResult.proposals.length > 0
                      ? 'Proposed rule refactoring'
                      : 'No changes needed'}
                  </h3>
                  {refactorResult.summary && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
                      {refactorResult.summary}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setRefactorResult(null)}
                className="text-slate-400 hover:text-slate-600 shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {refactorResult.proposals.length > 0 && (
              <div className="px-6 py-4 space-y-2">
                {refactorResult.proposals.map((proposal, index) => (
                  <label
                    key={`${proposal.action}-${proposal.ruleId}-${index}`}
                    className={cn(
                      'flex items-start gap-3 px-3 py-3 rounded-md border cursor-pointer',
                      selectedProposals.has(index)
                        ? 'border-teal-300 dark:border-teal-700 bg-teal-50/50 dark:bg-teal-900/10'
                        : 'border-slate-200 dark:border-slate-700 opacity-60',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selectedProposals.has(index)}
                      onChange={() => toggleProposal(index)}
                      className="mt-1 accent-teal-600"
                    />
                    <div className="min-w-0">
                      <span
                        className={cn(
                          'text-[11px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border',
                          proposal.action === 'delete'
                            ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'
                            : 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-300 dark:border-teal-800',
                        )}
                      >
                        {proposal.action === 'delete' ? 'Delete' : 'Update'}
                      </span>
                      <p
                        className={cn(
                          'text-sm mt-1.5',
                          proposal.action === 'delete'
                            ? 'text-slate-500 dark:text-slate-400 line-through'
                            : 'text-slate-500 dark:text-slate-400',
                        )}
                      >
                        {proposal.currentText}
                      </p>
                      {proposal.action === 'update' && (
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 mt-1">
                          → {proposal.text}
                          {proposal.category &&
                            proposal.category !== proposal.currentCategory && (
                              <span className="ml-2 text-xs font-normal text-slate-500">
                                (moved to {proposal.category})
                              </span>
                            )}
                        </p>
                      )}
                      {proposal.reason && (
                        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                          {proposal.reason}
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-800">
              <button
                onClick={() => setRefactorResult(null)}
                className="px-4 py-2 rounded-md text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                {refactorResult.proposals.length > 0 ? 'Cancel' : 'Close'}
              </button>
              {refactorResult.proposals.length > 0 && (
                <button
                  onClick={handleApplyRefactor}
                  disabled={applyingRefactor || selectedProposals.size === 0}
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                >
                  {applyingRefactor && <Spinner size="sm" tone="current" />}
                  Apply {selectedProposals.size} selected
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Rule conflict dialog ── */}
      {conflictResult && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setConflictResult(null)}
        >
          <div
            className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-2">
                {conflictResult.findings.length > 0 ? (
                  <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
                ) : (
                  <Sparkles className="w-5 h-5 text-teal-500 shrink-0" />
                )}
                <div>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    {conflictResult.findings.length > 0
                      ? 'Similar or conflicting rules found'
                      : 'Suggested improvements to your rule'}
                  </h3>
                  {conflictResult.summary && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
                      {conflictResult.summary}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setConflictResult(null)}
                className="text-slate-400 hover:text-slate-600 shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-5">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  Your new rule
                </p>
                <p className="text-sm text-slate-900 dark:text-slate-100 px-3 py-2 rounded-md bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                  {ruleText.trim()}
                </p>
              </div>

              {conflictResult.optimizedText &&
                conflictResult.optimizedText.trim() !== ruleText.trim() && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                      Suggested wording
                    </p>
                    <div className="flex items-start justify-between gap-3 px-3 py-2 rounded-md border border-teal-200 dark:border-teal-800 bg-teal-50/50 dark:bg-teal-900/10">
                      <p className="text-sm text-slate-900 dark:text-slate-100">
                        {conflictResult.optimizedText}
                      </p>
                      <button
                        onClick={() => handleAddOptimized()}
                        disabled={savingRule}
                        className="shrink-0 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                      >
                        Use this
                      </button>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Spelling and phrasing adjusted so AI agents can apply the rule unambiguously.
                    </p>
                  </div>
                )}

              {conflictResult.findings.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Related existing rules
                </p>
                <div className="space-y-2">
                  {conflictResult.findings.map((finding) => (
                    <div
                      key={finding.ruleId}
                      className="px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'text-[11px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border',
                            RELATION_BADGES[finding.relation] ?? RELATION_BADGES.overlap,
                          )}
                        >
                          {finding.relation}
                        </span>
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {finding.ruleText}
                        </p>
                      </div>
                      {finding.explanation && (
                        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                          {finding.explanation}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              )}

              {conflictResult.suggestions.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    Suggestions
                  </p>
                  <div className="space-y-2">
                    {conflictResult.suggestions.map((suggestion, i) => (
                      <div
                        key={i}
                        className="flex items-start justify-between gap-3 px-3 py-2 rounded-md border border-teal-200 dark:border-teal-800 bg-teal-50/50 dark:bg-teal-900/10"
                      >
                        <div>
                          <p className="text-xs font-medium text-teal-700 dark:text-teal-300">
                            {suggestion.action === 'update_existing'
                              ? 'Update existing rule to:'
                              : 'Add as a new rule:'}
                          </p>
                          <p className="text-sm text-slate-900 dark:text-slate-100 mt-0.5">
                            {suggestion.suggestedText}
                          </p>
                          {suggestion.rationale && (
                            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                              {suggestion.rationale}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleApplySuggestion(suggestion)}
                          disabled={savingRule}
                          className="shrink-0 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                        >
                          Apply
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-800">
              <button
                onClick={() => setConflictResult(null)}
                className="px-4 py-2 rounded-md text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAnyway}
                disabled={savingRule}
                className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-slate-600 hover:bg-slate-700 disabled:opacity-50"
              >
                {savingRule && <Spinner size="sm" tone="current" />}
                Add as written
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
